import { useEffect, useRef, useState } from "react";
import "@/App.css";
import axios from "axios";
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import Marquee from "@/components/Marquee";
import MenuNav from "@/components/MenuNav";
import Gallery from "@/components/Gallery";
import MenuSection from "@/components/MenuSection";
import MenuBook from "@/components/MenuBook";
import Ornament from "@/components/Ornament";
import Reviews from "@/components/Reviews";
import Visit from "@/components/Visit";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function App() {
  const [categories, setCategories] = useState([]);
  const [info, setInfo] = useState(null);
  const [query, setQuery] = useState("");
  const [vegOnly, setVegOnly] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/menu`)
      .then((res) => setCategories(res.data.categories))
      .catch((e) => console.error("menu fetch failed", e));
    axios
      .get(`${API}/restaurant`)
      .then((res) => setInfo(res.data))
      .catch((e) => console.error("restaurant fetch failed", e));
  }, []);

  const q = query.trim().toLowerCase();
  const visibleCategories = categories
    .map((cat) => ({
      ...cat,
      groups: cat.groups
        .map((group) => ({
          ...group,
          items: group.items.filter(
            (item) =>
              (!q || item.name.toLowerCase().includes(q)) &&
              (!vegOnly || item.veg)
          ),
        }))
        .filter((group) => group.items.length > 0),
    }))
    .filter((cat) => cat.groups.length > 0);
  const resultCount = visibleCategories.reduce(
    (sum, cat) =>
      sum + cat.groups.reduce((s, group) => s + group.items.length, 0),
    0
  );

  const bookApiRef = useRef(null);

  const handleSelectCategory = (slug) => {
    setQuery(""); // clear search so the book is mounted and can flip
    document.getElementById("menu")?.scrollIntoView({ behavior: "smooth" });
    // wait a tick for the book to mount if it was hidden by search
    setTimeout(() => bookApiRef.current?.flipToCategory(slug), 60);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("revealed");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    document
      .querySelectorAll("[data-reveal]")
      .forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [categories]);

  return (
    <div
      data-testid="app-root"
      className="bg-[#0B1319] text-[#F3EAD8] font-sans antialiased min-h-screen"
    >
      <div className="grain-overlay" aria-hidden="true"></div>
      <Navbar info={info} onSelectCategory={handleSelectCategory} />
      <Hero />
      <Marquee />
      <MenuNav
        query={query}
        setQuery={setQuery}
        vegOnly={vegOnly}
        setVegOnly={setVegOnly}
        resultCount={resultCount}
        onSelectCategory={handleSelectCategory}
      />
      <Gallery />
      <section
        id="menu"
        data-testid="menu-book-section"
        className="py-16 px-6 max-w-7xl mx-auto border-t border-[#1E313D] scroll-mt-36"
      >
        <div className="text-center max-w-2xl mx-auto mb-10" data-reveal>
          <span className="text-xs uppercase tracking-widest text-[#D4AF37] font-semibold">
            The Menu Book
          </span>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#F3EAD8] mt-1">
            Flip Through Our Menu
          </h2>
          <p className="text-sm text-[#A8B8C0] mt-2">
            Turn the pages like our printed menu book — drag a page corner or
            use the arrows on the sides.
          </p>
          <Ornament />
        </div>
        {query ? (
          visibleCategories.length > 0 ? (
            <div data-testid="menu-search-results">
              {visibleCategories.map((cat) => (
                <MenuSection key={cat.slug} category={cat} />
              ))}
            </div>
          ) : (
            <p
              data-testid="menu-no-results"
              className="py-10 text-center text-sm text-[#A8B8C0]"
            >
              No dishes found for “{query}”. Try “biryani”, “paneer” or
              “tikka”.
            </p>
          )
        ) : visibleCategories.length > 0 ? (
          <MenuBook
            categories={visibleCategories}
            info={info}
            apiRef={bookApiRef}
          />
        ) : (
          <p
            data-testid="menu-no-results"
            className="py-10 text-center text-sm text-[#A8B8C0]"
          >
            No vegetarian dishes found.
          </p>
        )}
      </section>
      <Reviews />
      <Visit info={info} />
      <Footer info={info} onSelectCategory={handleSelectCategory} />
    </div>
  );
}
